import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { TreeSelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/TreeSelectCombobox/TreeSelectCombobox";
import type { IndustryNodeId } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import InclusionGroup from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/atoms/InclusionGroup";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { createIndustryTreeViewModel } from "app/pages/sales-intelligence/modules/industry/view/industry-tree/factory";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { addNode } from "app/pages/sales-intelligence/utils/taxonomy-tree/tree";

import type IndustryTaxonomyFilterModel from "../../../models/IndustryTaxonomyFilter/IndustryTaxonomyFilterModel";
import leadsSearchFiltersI18nKeys from "../../../utils/i18n";
import { FilterContentContainer } from "../styles";

import { IndustryTaxonomyDemoReturnButton } from "./IndustryTaxonomyDemoReturnButton";
import { IndustryTaxonomySuggestions } from "./IndustryTaxonomySuggestions";

const IndustryTaxonomyFilter = ({
    filter,
    value,
    isExpanded = false,
    onFilterUpdate,
}: IFilterViewBaseProps<IndustryTaxonomyFilterModel>) => {
    const { translate, getLocale } = useTranslate();
    const locale = getLocale();

    // Defaulting lives in the model (`fromApiValue`); `value` is already the defaulted, canonical local value.
    const persisted = value ?? filter.fromApiValue();
    const { values } = persisted;

    // The model does not persist inclusion while the selection is empty (an empty selection has no DTO), so the
    // radio is driven by local state to stay responsive on click. It re-syncs whenever the persisted inclusion
    // changes — a reset or a restored saved search must not leave the radio showing the previous inclusion.
    // Compared by value, not by reference: `value`'s identity churns on every update, and re-syncing on identity
    // would undo a click made while the selection is still empty.
    const [inclusion, setInclusion] = useState<InclusionOption>(persisted.inclusion);
    const [lastPersistedInclusion, setLastPersistedInclusion] = useState<InclusionOption>(persisted.inclusion);

    if (lastPersistedInclusion !== persisted.inclusion) {
        setLastPersistedInclusion(persisted.inclusion);
        setInclusion(persisted.inclusion);
    }

    const searchHits = useCallback(
        (query: string) => filter.searchIndex.searchHits({ taxonomy: filter.options, query, locale }),
        [filter, locale],
    );

    const viewModel = useMemo(
        () => createIndustryTreeViewModel({ taxonomy: filter.options, value: values, searchHits }),
        [filter.options, values, searchHits],
    );

    const handleInclusionChange = useCallback(
        (nextInclusion: InclusionOption) => {
            setInclusion(nextInclusion);

            if (values.length > 0) {
                onFilterUpdate({ inclusion: nextInclusion, values });
            }
        },
        [onFilterUpdate, values],
    );

    const handleValuesChange = useCallback(
        (nextValues: readonly IndustryNodeId[]) => {
            onFilterUpdate({ inclusion, values: nextValues });
        },
        [onFilterUpdate, inclusion],
    );

    const handleApplySuggestion = useCallback(
        (id: IndustryNodeId) => {
            const nextValues = addNode(values, id, filter.options);

            if (nextValues !== values) {
                handleValuesChange(nextValues);
            }
        },
        [values, filter.options, handleValuesChange],
    );

    return (
        <FilterContentContainer>
            <InclusionGroup
                value={inclusion}
                onChange={handleInclusionChange}
            />

            <IndustryTaxonomySuggestions
                values={values}
                inclusion={inclusion}
                taxonomy={filter.options}
                isExpanded={isExpanded}
                onApply={handleApplySuggestion}
            />

            <TreeSelectCombobox
                dataAutomation="industry-taxonomy-filter"
                viewModel={viewModel}
                buttonText={translate(leadsSearchFiltersI18nKeys.industryTaxonomyDdButtonText())}
                searchPlaceholder={translate(leadsSearchFiltersI18nKeys.industryTaxonomyDdPlaceholder())}
                expandLabel={translate(leadsSearchFiltersI18nKeys.industryTaxonomyExpandAria())}
                collapseLabel={translate(leadsSearchFiltersI18nKeys.industryTaxonomyCollapseAria())}
                onChange={handleValuesChange}
            />

            <IndustryTaxonomyDemoReturnButton />
        </FilterContentContainer>
    );
};

export default IndustryTaxonomyFilter;
